/**
 * AS PALAVRAS DOS WORKSPACES — a matriz, os outros workspaces da conta, e o
 * seletor que anda entre eles.
 *
 * Dicionário da FEATURE e não da página: a aba `Configurações › Workspaces`, o
 * seletor no topo do menu, a oferta de estrutura na tela inicial e o aviso nas
 * telas de cobrança leem todos daqui.
 *
 * ── AS RECUSAS SÃO TRADUZIDAS AQUI, E SÓ AQUI ─────────────────────────────
 *
 * O servidor responde CÓDIGOS (`WORKSPACE_LIMIT`, `NOT_HQ`), nunca frases: uma
 * frase escolhida no servidor é uma frase na língua errada para quem abriu o
 * painel em grego. `error.*` é o mapa, e `error.generic` é o que sobra para um
 * código que esta versão da tela ainda não conhece.
 */
export const organizationMessages = {
	en: {
		org: {
			title: "Workspaces",
			subtitle:
				"Each workspace has its own team, channels and customers. Everything is billed to this account, and credits are shared.",
			standalone: {
				title: "Only this workspace, for now",
				body: "Create a workspace for each store or unit. It is free: people, channels and credits are billed here, in this account.",
			},
			hq: {
				count: "{n} of {max} workspaces",
				countUnlimited: "{n} workspaces",
				cycleFrom: "Credits since {date}",
				noCycle: "No open cycle yet",
			},
			table: {
				name: "Workspace",
				people: "People",
				spent: "Credits",
				hqBadge: "Main",
				here: "You are here",
				total: "Total",
				open: "Open",
			},
			add: {
				open: "New workspace",
				title: "New workspace",
				name: "Name",
				placeholder: "Downtown store",
				copy: "Copy this workspace's structure",
				copyHint:
					"Screens, funnels, automations and agents. Customers, conversations and people do not travel.",
				submit: "Create workspace",
				done: "Workspace created.",
				structureFailed: "Created, but the structure was not copied: {reason}",
			},
			structure: {
				title: "Apply this workspace's structure",
				body: "Copies the screens, funnels, automations and agents of the main workspace to the ones you pick.",
				apply: "Apply",
				done: "Applied to {n} workspace. | Applied to {n} workspaces.",
				failedOn: "{name}: not applied ({reason})",
			},
			unit: {
				badge: "Workspace of {hq}",
				body: "Plan, billing and credits belong to {hq}. The team of this workspace only sees this workspace.",
			},
			offer: {
				title: "Apply the structure of {hq}?",
				body: "This workspace was created empty. Copy the screens, funnels, automations and agents of the main workspace now, or start from scratch.",
				apply: "Apply",
				dismiss: "Dismiss",
				done: "Structure applied.",
				failed: "The structure was not applied: {reason}",
				leftOut: "Structure applied. Left out, for a channel this workspace does not have: {rules}",
				missingSecrets:
					"Structure applied. Functions need keys this workspace has not set yet: {names} — fill them in Settings → Secrets, or ask Ben.",
			},
			archive: {
				action: "Archive",
				title: "Archive {name}?",
				body: "The workspace is frozen: nobody gets in and nothing is sent. Its data is kept.",
				confirm: "Archive",
				done: "Workspace archived.",
			},
			switcher: {
				label: "Switch workspace",
				main: "Main",
			},
			billing: {
				byHq: "This workspace is billed by {hq}. Plan, invoices and credits are managed there.",
			},
			error: {
				NOT_HQ: "Only the main workspace can do this.",
				WORKSPACE_LIMIT: "This account has reached its workspace limit.",
				NOT_A_UNIT: "That workspace is not part of this account.",
				NO_ORGANIZATION: "This workspace has no other workspaces.",
				generic: "It did not work. Try again.",
			},
		},
	},
	"pt-BR": {
		org: {
			title: "Workspaces",
			subtitle:
				"Cada workspace tem a própria equipe, canais e clientes. Tudo é cobrado nesta conta, e os créditos são compartilhados.",
			standalone: {
				title: "Só este workspace, por enquanto",
				body: "Crie um workspace para cada loja ou unidade. É grátis: pessoas, canais e créditos são cobrados aqui, nesta conta.",
			},
			hq: {
				count: "{n} de {max} workspaces",
				countUnlimited: "{n} workspaces",
				cycleFrom: "Créditos desde {date}",
				noCycle: "Nenhum ciclo aberto ainda",
			},
			table: {
				name: "Workspace",
				people: "Pessoas",
				spent: "Créditos",
				hqBadge: "Matriz",
				here: "Você está aqui",
				total: "Total",
				open: "Abrir",
			},
			add: {
				open: "Novo workspace",
				title: "Novo workspace",
				name: "Nome",
				placeholder: "Loja Centro",
				copy: "Copiar a estrutura deste workspace",
				copyHint:
					"Telas, funis, automações e agentes. Clientes, conversas e pessoas não vão junto.",
				submit: "Criar workspace",
				done: "Workspace criado.",
				structureFailed: "Criado, mas a estrutura não foi copiada: {reason}",
			},
			structure: {
				title: "Aplicar a estrutura deste workspace",
				body: "Copia as telas, funis, automações e agentes da matriz para os workspaces que você escolher.",
				apply: "Aplicar",
				done: "Aplicada em {n} workspace. | Aplicada em {n} workspaces.",
				failedOn: "{name}: não aplicada ({reason})",
			},
			unit: {
				badge: "Workspace de {hq}",
				body: "Plano, cobrança e créditos são de {hq}. A equipe deste workspace só vê este workspace.",
			},
			offer: {
				title: "Aplicar a estrutura de {hq}?",
				body: "Este workspace nasceu vazio. Copie agora as telas, funis, automações e agentes da matriz, ou comece do zero.",
				apply: "Aplicar",
				dismiss: "Dispensar",
				done: "Estrutura aplicada.",
				failed: "A estrutura não foi aplicada: {reason}",
				leftOut:
					"Estrutura aplicada. Ficaram de fora, por citarem um canal que este workspace não tem: {rules}",
				missingSecrets:
					"Estrutura aplicada. As funções pedem chaves que este workspace ainda não tem: {names} — preencha em Ajustes → Segredos, ou peça ao Ben.",
			},
			archive: {
				action: "Arquivar",
				title: "Arquivar {name}?",
				body: "O workspace fica congelado: ninguém entra e nada é enviado. Os dados são preservados.",
				confirm: "Arquivar",
				done: "Workspace arquivado.",
			},
			switcher: {
				label: "Trocar de workspace",
				main: "Matriz",
			},
			billing: {
				byHq: "A cobrança deste workspace é feita pela matriz {hq}. Plano, faturas e créditos ficam lá.",
			},
			error: {
				NOT_HQ: "Só a matriz pode fazer isso.",
				WORKSPACE_LIMIT: "Esta conta chegou ao limite de workspaces.",
				NOT_A_UNIT: "Esse workspace não é desta conta.",
				NO_ORGANIZATION: "Este workspace não tem outros workspaces.",
				generic: "Não deu certo. Tente de novo.",
			},
		},
	},
	el: {
		org: {
			title: "Χώροι εργασίας",
			subtitle:
				"Κάθε χώρος εργασίας έχει τη δική του ομάδα, κανάλια και πελάτες. Όλα χρεώνονται σε αυτόν τον λογαριασμό και οι μονάδες μοιράζονται.",
			standalone: {
				title: "Μόνο αυτός ο χώρος εργασίας, προς το παρόν",
				body: "Δημιουργήστε έναν χώρο εργασίας για κάθε κατάστημα ή μονάδα. Είναι δωρεάν: άτομα, κανάλια και μονάδες χρεώνονται εδώ, σε αυτόν τον λογαριασμό.",
			},
			hq: {
				count: "{n} από {max} χώρους εργασίας",
				countUnlimited: "{n} χώροι εργασίας",
				cycleFrom: "Μονάδες από {date}",
				noCycle: "Δεν υπάρχει ανοιχτός κύκλος ακόμη",
			},
			table: {
				name: "Χώρος εργασίας",
				people: "Άτομα",
				spent: "Μονάδες",
				hqBadge: "Κεντρικός",
				here: "Είστε εδώ",
				total: "Σύνολο",
				open: "Άνοιγμα",
			},
			add: {
				open: "Νέος χώρος εργασίας",
				title: "Νέος χώρος εργασίας",
				name: "Όνομα",
				placeholder: "Κατάστημα κέντρου",
				copy: "Αντιγραφή της δομής αυτού του χώρου εργασίας",
				copyHint:
					"Οθόνες, χοάνες, αυτοματισμοί και πράκτορες. Πελάτες, συνομιλίες και άτομα δεν μεταφέρονται.",
				submit: "Δημιουργία χώρου εργασίας",
				done: "Ο χώρος εργασίας δημιουργήθηκε.",
				structureFailed: "Δημιουργήθηκε, αλλά η δομή δεν αντιγράφηκε: {reason}",
			},
			structure: {
				title: "Εφαρμογή της δομής αυτού του χώρου εργασίας",
				body: "Αντιγράφει οθόνες, χοάνες, αυτοματισμούς και πράκτορες του κεντρικού χώρου στους χώρους που επιλέγετε.",
				apply: "Εφαρμογή",
				done: "Εφαρμόστηκε σε {n} χώρο εργασίας. | Εφαρμόστηκε σε {n} χώρους εργασίας.",
				failedOn: "{name}: δεν εφαρμόστηκε ({reason})",
			},
			unit: {
				badge: "Χώρος εργασίας του {hq}",
				body: "Το πρόγραμμα, η χρέωση και οι μονάδες ανήκουν στο {hq}. Η ομάδα αυτού του χώρου βλέπει μόνο αυτόν τον χώρο.",
			},
			offer: {
				title: "Εφαρμογή της δομής του {hq};",
				body: "Αυτός ο χώρος εργασίας δημιουργήθηκε κενός. Αντιγράψτε τώρα οθόνες, χοάνες, αυτοματισμούς και πράκτορες του κεντρικού χώρου ή ξεκινήστε από την αρχή.",
				apply: "Εφαρμογή",
				dismiss: "Απόρριψη",
				done: "Η δομή εφαρμόστηκε.",
				failed: "Η δομή δεν εφαρμόστηκε: {reason}",
				leftOut:
					"Η δομή εφαρμόστηκε. Έμειναν εκτός, επειδή αναφέρουν κανάλι που δεν έχει αυτός ο χώρος εργασίας: {rules}",
				missingSecrets:
					"Η δομή εφαρμόστηκε. Οι λειτουργίες χρειάζονται κλειδιά που δεν έχει ακόμη αυτός ο χώρος εργασίας: {names} — συμπλήρωσέ τα στις Ρυθμίσεις → Μυστικά ή ζήτησέ το από τον Ben.",
			},
			archive: {
				action: "Αρχειοθέτηση",
				title: "Αρχειοθέτηση του {name};",
				body: "Ο χώρος εργασίας παγώνει: κανείς δεν μπαίνει και τίποτα δεν αποστέλλεται. Τα δεδομένα διατηρούνται.",
				confirm: "Αρχειοθέτηση",
				done: "Ο χώρος εργασίας αρχειοθετήθηκε.",
			},
			switcher: {
				label: "Αλλαγή χώρου εργασίας",
				main: "Κεντρικός",
			},
			billing: {
				byHq: "Αυτός ο χώρος εργασίας χρεώνεται από το {hq}. Το πρόγραμμα, τα τιμολόγια και οι μονάδες διαχειρίζονται εκεί.",
			},
			error: {
				NOT_HQ: "Μόνο ο κεντρικός χώρος εργασίας μπορεί να το κάνει αυτό.",
				WORKSPACE_LIMIT: "Ο λογαριασμός έφτασε το όριο χώρων εργασίας.",
				NOT_A_UNIT: "Αυτός ο χώρος εργασίας δεν ανήκει σε αυτόν τον λογαριασμό.",
				NO_ORGANIZATION: "Αυτός ο χώρος εργασίας δεν έχει άλλους χώρους.",
				generic: "Δεν πέτυχε. Δοκιμάστε ξανά.",
			},
		},
	},
	es: {
		org: {
			title: "Workspaces",
			subtitle:
				"Cada workspace tiene su propio equipo, canales y clientes. Todo se factura en esta cuenta y los créditos se comparten.",
			standalone: {
				title: "Solo este workspace, por ahora",
				body: "Cree un workspace para cada tienda o unidad. Es gratis: personas, canales y créditos se facturan aquí, en esta cuenta.",
			},
			hq: {
				count: "{n} de {max} workspaces",
				countUnlimited: "{n} workspaces",
				cycleFrom: "Créditos desde {date}",
				noCycle: "Todavía no hay un ciclo abierto",
			},
			table: {
				name: "Workspace",
				people: "Personas",
				spent: "Créditos",
				hqBadge: "Principal",
				here: "Usted está aquí",
				total: "Total",
				open: "Abrir",
			},
			add: {
				open: "Nuevo workspace",
				title: "Nuevo workspace",
				name: "Nombre",
				placeholder: "Tienda Centro",
				copy: "Copiar la estructura de este workspace",
				copyHint:
					"Pantallas, embudos, automatizaciones y agentes. Clientes, conversaciones y personas no viajan.",
				submit: "Crear workspace",
				done: "Workspace creado.",
				structureFailed: "Creado, pero la estructura no se copió: {reason}",
			},
			structure: {
				title: "Aplicar la estructura de este workspace",
				body: "Copia las pantallas, embudos, automatizaciones y agentes del workspace principal a los que elija.",
				apply: "Aplicar",
				done: "Aplicada en {n} workspace. | Aplicada en {n} workspaces.",
				failedOn: "{name}: no aplicada ({reason})",
			},
			unit: {
				badge: "Workspace de {hq}",
				body: "Plan, facturación y créditos son de {hq}. El equipo de este workspace solo ve este workspace.",
			},
			offer: {
				title: "¿Aplicar la estructura de {hq}?",
				body: "Este workspace nació vacío. Copie ahora las pantallas, embudos, automatizaciones y agentes del workspace principal, o empiece desde cero.",
				apply: "Aplicar",
				dismiss: "Descartar",
				done: "Estructura aplicada.",
				failed: "La estructura no se aplicó: {reason}",
				leftOut:
					"Estructura aplicada. Quedaron fuera, por citar un canal que este espacio de trabajo no tiene: {rules}",
				missingSecrets:
					"Estructura aplicada. Las funciones piden claves que este espacio de trabajo aún no tiene: {names} — complétalas en Ajustes → Secretos, o pídeselo a Ben.",
			},
			archive: {
				action: "Archivar",
				title: "¿Archivar {name}?",
				body: "El workspace queda congelado: nadie entra y no se envía nada. Los datos se conservan.",
				confirm: "Archivar",
				done: "Workspace archivado.",
			},
			switcher: {
				label: "Cambiar de workspace",
				main: "Principal",
			},
			billing: {
				byHq: "Este workspace lo factura {hq}. El plan, las facturas y los créditos se gestionan allí.",
			},
			error: {
				NOT_HQ: "Solo el workspace principal puede hacer esto.",
				WORKSPACE_LIMIT: "Esta cuenta llegó al límite de workspaces.",
				NOT_A_UNIT: "Ese workspace no es de esta cuenta.",
				NO_ORGANIZATION: "Este workspace no tiene otros workspaces.",
				generic: "No funcionó. Inténtelo de nuevo.",
			},
		},
	},
	fr: {
		org: {
			title: "Espaces de travail",
			subtitle:
				"Chaque espace de travail a sa propre équipe, ses canaux et ses clients. Tout est facturé sur ce compte, et les crédits sont partagés.",
			standalone: {
				title: "Seulement cet espace, pour l'instant",
				body: "Créez un espace de travail pour chaque magasin ou unité. C'est gratuit : personnes, canaux et crédits sont facturés ici, sur ce compte.",
			},
			hq: {
				count: "{n} sur {max} espaces de travail",
				countUnlimited: "{n} espaces de travail",
				cycleFrom: "Crédits depuis le {date}",
				noCycle: "Aucun cycle ouvert pour l'instant",
			},
			table: {
				name: "Espace de travail",
				people: "Personnes",
				spent: "Crédits",
				hqBadge: "Principal",
				here: "Vous êtes ici",
				total: "Total",
				open: "Ouvrir",
			},
			add: {
				open: "Nouvel espace de travail",
				title: "Nouvel espace de travail",
				name: "Nom",
				placeholder: "Magasin du centre",
				copy: "Copier la structure de cet espace",
				copyHint:
					"Écrans, entonnoirs, automatisations et agents. Clients, conversations et personnes ne suivent pas.",
				submit: "Créer l'espace de travail",
				done: "Espace de travail créé.",
				structureFailed: "Créé, mais la structure n'a pas été copiée : {reason}",
			},
			structure: {
				title: "Appliquer la structure de cet espace",
				body: "Copie les écrans, entonnoirs, automatisations et agents de l'espace principal vers ceux que vous choisissez.",
				apply: "Appliquer",
				done: "Appliquée à {n} espace de travail. | Appliquée à {n} espaces de travail.",
				failedOn: "{name} : non appliquée ({reason})",
			},
			unit: {
				badge: "Espace de travail de {hq}",
				body: "Le forfait, la facturation et les crédits appartiennent à {hq}. L'équipe de cet espace ne voit que cet espace.",
			},
			offer: {
				title: "Appliquer la structure de {hq} ?",
				body: "Cet espace de travail est né vide. Copiez maintenant les écrans, entonnoirs, automatisations et agents de l'espace principal, ou partez de zéro.",
				apply: "Appliquer",
				dismiss: "Ignorer",
				done: "Structure appliquée.",
				failed: "La structure n'a pas été appliquée : {reason}",
				leftOut:
					"Structure appliquée. Laissées de côté, car elles citent un canal que cet espace de travail n'a pas : {rules}",
				missingSecrets:
					"Structure appliquée. Les fonctions demandent des clés que cet espace de travail n'a pas encore : {names} — renseignez-les dans Réglages → Secrets, ou demandez à Ben.",
			},
			archive: {
				action: "Archiver",
				title: "Archiver {name} ?",
				body: "L'espace de travail est gelé : personne n'entre et rien n'est envoyé. Les données sont conservées.",
				confirm: "Archiver",
				done: "Espace de travail archivé.",
			},
			switcher: {
				label: "Changer d'espace de travail",
				main: "Principal",
			},
			billing: {
				byHq: "Cet espace de travail est facturé par {hq}. Forfait, factures et crédits se gèrent là-bas.",
			},
			error: {
				NOT_HQ: "Seul l'espace principal peut faire cela.",
				WORKSPACE_LIMIT: "Ce compte a atteint sa limite d'espaces de travail.",
				NOT_A_UNIT: "Cet espace de travail n'appartient pas à ce compte.",
				NO_ORGANIZATION: "Cet espace de travail n'a pas d'autres espaces.",
				generic: "Cela n'a pas marché. Réessayez.",
			},
		},
	},
	it: {
		org: {
			title: "Workspace",
			subtitle:
				"Ogni workspace ha il proprio team, i propri canali e clienti. Tutto è fatturato su questo account e i crediti sono condivisi.",
			standalone: {
				title: "Solo questo workspace, per ora",
				body: "Crea un workspace per ogni negozio o unità. È gratis: persone, canali e crediti sono fatturati qui, su questo account.",
			},
			hq: {
				count: "{n} di {max} workspace",
				countUnlimited: "{n} workspace",
				cycleFrom: "Crediti dal {date}",
				noCycle: "Ancora nessun ciclo aperto",
			},
			table: {
				name: "Workspace",
				people: "Persone",
				spent: "Crediti",
				hqBadge: "Principale",
				here: "Sei qui",
				total: "Totale",
				open: "Apri",
			},
			add: {
				open: "Nuovo workspace",
				title: "Nuovo workspace",
				name: "Nome",
				placeholder: "Negozio centro",
				copy: "Copia la struttura di questo workspace",
				copyHint:
					"Schermate, funnel, automazioni e agenti. Clienti, conversazioni e persone non vengono copiati.",
				submit: "Crea workspace",
				done: "Workspace creato.",
				structureFailed: "Creato, ma la struttura non è stata copiata: {reason}",
			},
			structure: {
				title: "Applica la struttura di questo workspace",
				body: "Copia schermate, funnel, automazioni e agenti del workspace principale in quelli che scegli.",
				apply: "Applica",
				done: "Applicata a {n} workspace.",
				failedOn: "{name}: non applicata ({reason})",
			},
			unit: {
				badge: "Workspace di {hq}",
				body: "Piano, fatturazione e crediti appartengono a {hq}. Il team di questo workspace vede solo questo workspace.",
			},
			offer: {
				title: "Applicare la struttura di {hq}?",
				body: "Questo workspace è nato vuoto. Copia ora schermate, funnel, automazioni e agenti del workspace principale, oppure parti da zero.",
				apply: "Applica",
				dismiss: "Ignora",
				done: "Struttura applicata.",
				failed: "La struttura non è stata applicata: {reason}",
				leftOut:
					"Struttura applicata. Escluse, perché citano un canale che questo spazio di lavoro non ha: {rules}",
				missingSecrets:
					"Struttura applicata. Le funzioni chiedono chiavi che questo spazio di lavoro non ha ancora: {names} — compilale in Impostazioni → Segreti, o chiedi a Ben.",
			},
			archive: {
				action: "Archivia",
				title: "Archiviare {name}?",
				body: "Il workspace viene congelato: nessuno entra e non si invia nulla. I dati sono conservati.",
				confirm: "Archivia",
				done: "Workspace archiviato.",
			},
			switcher: {
				label: "Cambia workspace",
				main: "Principale",
			},
			billing: {
				byHq: "Questo workspace è fatturato da {hq}. Piano, fatture e crediti si gestiscono lì.",
			},
			error: {
				NOT_HQ: "Solo il workspace principale può farlo.",
				WORKSPACE_LIMIT: "Questo account ha raggiunto il limite di workspace.",
				NOT_A_UNIT: "Quel workspace non appartiene a questo account.",
				NO_ORGANIZATION: "Questo workspace non ha altri workspace.",
				generic: "Non ha funzionato. Riprova.",
			},
		},
	},
	de: {
		org: {
			title: "Workspaces",
			subtitle:
				"Jeder Workspace hat ein eigenes Team, eigene Kanäle und Kunden. Alles wird über dieses Konto abgerechnet, und die Credits werden geteilt.",
			standalone: {
				title: "Vorerst nur dieser Workspace",
				body: "Legen Sie für jedes Geschäft oder jede Einheit einen Workspace an. Das ist kostenlos: Personen, Kanäle und Credits werden hier, über dieses Konto, abgerechnet.",
			},
			hq: {
				count: "{n} von {max} Workspaces",
				countUnlimited: "{n} Workspaces",
				cycleFrom: "Credits seit {date}",
				noCycle: "Noch kein offener Zyklus",
			},
			table: {
				name: "Workspace",
				people: "Personen",
				spent: "Credits",
				hqBadge: "Haupt",
				here: "Sie sind hier",
				total: "Gesamt",
				open: "Öffnen",
			},
			add: {
				open: "Neuer Workspace",
				title: "Neuer Workspace",
				name: "Name",
				placeholder: "Filiale Innenstadt",
				copy: "Struktur dieses Workspace kopieren",
				copyHint:
					"Ansichten, Funnels, Automationen und Agenten. Kunden, Gespräche und Personen werden nicht übernommen.",
				submit: "Workspace anlegen",
				done: "Workspace angelegt.",
				structureFailed: "Angelegt, aber die Struktur wurde nicht kopiert: {reason}",
			},
			structure: {
				title: "Struktur dieses Workspace anwenden",
				body: "Kopiert Ansichten, Funnels, Automationen und Agenten des Haupt-Workspace in die ausgewählten Workspaces.",
				apply: "Anwenden",
				done: "Auf {n} Workspace angewendet. | Auf {n} Workspaces angewendet.",
				failedOn: "{name}: nicht angewendet ({reason})",
			},
			unit: {
				badge: "Workspace von {hq}",
				body: "Tarif, Abrechnung und Credits gehören zu {hq}. Das Team dieses Workspace sieht nur diesen Workspace.",
			},
			offer: {
				title: "Struktur von {hq} anwenden?",
				body: "Dieser Workspace wurde leer angelegt. Kopieren Sie jetzt Ansichten, Funnels, Automationen und Agenten des Haupt-Workspace, oder beginnen Sie von vorn.",
				apply: "Anwenden",
				dismiss: "Verwerfen",
				done: "Struktur angewendet.",
				failed: "Die Struktur wurde nicht angewendet: {reason}",
				leftOut:
					"Struktur angewendet. Ausgelassen, weil sie einen Kanal nennen, den dieser Workspace nicht hat: {rules}",
				missingSecrets:
					"Struktur angewendet. Die Funktionen brauchen Schlüssel, die dieser Workspace noch nicht hat: {names} — trage sie unter Einstellungen → Geheimnisse ein oder frag Ben.",
			},
			archive: {
				action: "Archivieren",
				title: "{name} archivieren?",
				body: "Der Workspace wird eingefroren: niemand kommt hinein und nichts wird gesendet. Die Daten bleiben erhalten.",
				confirm: "Archivieren",
				done: "Workspace archiviert.",
			},
			switcher: {
				label: "Workspace wechseln",
				main: "Haupt",
			},
			billing: {
				byHq: "Dieser Workspace wird über {hq} abgerechnet. Tarif, Rechnungen und Credits werden dort verwaltet.",
			},
			error: {
				NOT_HQ: "Nur der Haupt-Workspace kann das tun.",
				WORKSPACE_LIMIT: "Dieses Konto hat die maximale Anzahl an Workspaces erreicht.",
				NOT_A_UNIT: "Dieser Workspace gehört nicht zu diesem Konto.",
				NO_ORGANIZATION: "Dieser Workspace hat keine weiteren Workspaces.",
				generic: "Das hat nicht geklappt. Bitte erneut versuchen.",
			},
		},
	},
} as const;
